import { useState, useEffect, useCallback } from 'react';
import { userFactsService } from '../services/userFactsService';
import { 
  UserFact, 
  UserFactsResponse, 
  UserFactInput,
  CreateUserFactsResponse,
  DeleteUserFactResponse 
} from '../types/userFacts';
import { useAuth } from '../context/AuthContext';

export interface UseUserFactsReturn {
  facts: UserFact[];
  loading: boolean;
  error: string | null;
  searchQuery: string;
  filteredFacts: UserFact[];
  total: number;
  
  // Actions
  loadFacts: (query?: string, topK?: number) => Promise<void>;
  searchFacts: (query: string) => void;
  createFact: (fact: UserFactInput, merge?: boolean) => Promise<CreateUserFactsResponse>;
  updateFact: (key: string, updates: Partial<UserFactInput>) => Promise<CreateUserFactsResponse>;
  deleteFact: (key: string) => Promise<DeleteUserFactResponse>;
  deleteAllFacts: () => Promise<void>;
  clearError: () => void;
  refreshFacts: () => Promise<void>;
}

export const useUserFacts = (): UseUserFactsReturn => {
  const { isAuthenticated } = useAuth();
  const [facts, setFacts] = useState<UserFact[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [total, setTotal] = useState(0);

  // Filter facts locally based on search query
  const filteredFacts = userFactsService.searchFactsLocally(facts, searchQuery);

  const loadFacts = useCallback(async (query?: string, topK: number = 100) => {
    if (!isAuthenticated) {
      setError('User not authenticated');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const response: UserFactsResponse = await userFactsService.getUserFacts(
        query,
        topK
      );
      
      setFacts(response.facts || []);
      setTotal(response.total || 0);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to load user facts';
      setError(errorMessage);
      console.error('Failed to load user facts:', err);
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated]);

  const searchFacts = useCallback((query: string) => {
    setSearchQuery(query);
  }, []);

  const createFact = useCallback(async (
    fact: UserFactInput, 
    merge: boolean = true
  ): Promise<CreateUserFactsResponse> => {
    if (!isAuthenticated) {
      throw new Error('User not authenticated');
    }

    setLoading(true);
    setError(null);

    try {
      // Validate the fact input
      const validation = userFactsService.validateFactInput(fact);
      if (!validation.valid) {
        throw new Error(`Invalid fact: ${validation.errors.join(', ')}`);
      }

      const response = await userFactsService.createFact(fact, merge);
      
      // Refresh facts after creation
      await loadFacts();
      
      return response;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to create fact';
      setError(errorMessage);
      console.error('Failed to create fact:', err);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated, loadFacts]);

  const updateFact = useCallback(async (
    key: string, 
    updates: Partial<UserFactInput>
  ): Promise<CreateUserFactsResponse> => {
    if (!isAuthenticated) {
      throw new Error('User not authenticated');
    }

    setLoading(true);
    setError(null);

    try {
      const response = await userFactsService.updateFact(key, updates);
      
      // Refresh facts after update
      await loadFacts();
      
      return response;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to update fact';
      setError(errorMessage);
      console.error('Failed to update fact:', err);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated, loadFacts]);

  const deleteFact = useCallback(async (
    key: string
  ): Promise<DeleteUserFactResponse> => {
    if (!isAuthenticated) {
      throw new Error('User not authenticated');
    }

    setLoading(true);
    setError(null);

    try {
      const response = await userFactsService.deleteFact(key);
      
      // Refresh facts after deletion
      await loadFacts();
      
      return response;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to delete fact';
      setError(errorMessage);
      console.error('Failed to delete fact:', err);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated, loadFacts]);

  const clearError = useCallback(() => {
    setError(null);
  }, []);

  const deleteAllFacts = useCallback(async () => {
    if (!isAuthenticated) {
      setError('User not authenticated');
      return;
    }

    if (facts.length === 0) {
      return; // Nothing to delete
    }

    setLoading(true);
    setError(null);

    try {
      // Delete all facts one by one
      const deletePromises = facts.map(fact => 
        userFactsService.deleteFact(fact.key)
      );
      
      await Promise.all(deletePromises);
      
      // Refresh facts after deletion
      await loadFacts();
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to delete all facts';
      setError(errorMessage);
      console.error('Failed to delete all facts:', err);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated, facts, loadFacts]);

  const refreshFacts = useCallback(async () => {
    await loadFacts();
  }, [loadFacts]);

  // Load facts on mount and when authentication changes
  useEffect(() => {
    if (isAuthenticated) {
      loadFacts();
    }
  }, [isAuthenticated, loadFacts]);

  return {
    facts,
    loading,
    error,
    searchQuery,
    filteredFacts,
    total,
    loadFacts,
    searchFacts,
    createFact,
    updateFact,
    deleteFact,
    deleteAllFacts,
    clearError,
    refreshFacts
  };
};
